import React from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/layout/MarketingNav";
import MarketingFooter from "@/components/layout/MarketingFooter";
import { MARKETING } from "@/constants/testIds";
import { ArrowUpRight, CheckCircle2, Clock, Flame, Quote, TrendingUp, Zap } from "lucide-react";

const CHECKOUT_URL = "https://capitalraisingsystem.polsia.app/playbook";

export default function CapitalRaising() {
  return (
    <div className="min-h-screen bg-stone-50">
      <MarketingNav />

      {/* Urgency bar */}
      <div className="border-b border-amber-300 bg-amber-50">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-6 py-2 lg:px-8">
          <Flame className="h-4 w-4 text-amber-700" />
          <span className="text-xs font-medium text-amber-900">
            Only skill you need to learn once — it will fund every business you build for the rest of your life.
          </span>
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-stone-200 bg-white">
        <div className="atlas-grid-bg atlas-radial-fade absolute inset-0 -z-10 opacity-70" />
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <div>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-3 py-1">
              <Zap className="h-3 w-3 text-emerald-600" />
              <span className="label-mono !text-[10px]">The AI Capital Raising System™</span>
            </div>
            <h1 className="font-display text-5xl font-bold leading-[1.02] tracking-tight text-slate-950 sm:text-6xl lg:text-7xl">
              Master the one skill<br />
              that funds<br />
              <span className="text-emerald-600">every business you'll ever build.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-stone-700">
              Most entrepreneurs die on the vine because they can't raise capital.
              Not because their idea was bad — because no one taught them the game.
              Learn it once. Use it forever.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a
                href={CHECKOUT_URL}
                target="_blank" rel="noreferrer"
                data-testid={MARKETING.capitalCheckout}
                className="btn-primary !px-8 !py-4 !text-base"
              >
                Get the Playbook Now <ArrowUpRight className="h-5 w-5" />
              </a>
              <div className="font-mono text-xs text-stone-500">
                Built by <span className="font-semibold text-slate-950">Lanre Ajakaiye</span>, founder
              </div>
            </div>
            <div className="mt-8 flex items-center gap-4 text-xs text-stone-500">
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5" />
                <span>Limited enrollment cohort</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                <span>Lifetime access</span>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="rounded-lg border border-stone-200 bg-white p-8 shadow-[0_20px_60px_-15px_rgb(15,23,42,0.15)]">
              <div className="label-mono mb-4">What's inside</div>
              <div className="space-y-4">
                {[
                  ["01", "Investor psychology decoded", "How VCs, angels & family offices actually think — and what makes them say yes."],
                  ["02", "Bulletproof pitch construction", "The 12-slide framework used in $8M+ raises."],
                  ["03", "The grant multiplier", "How to stack federal, state, foundation, and corporate grants."],
                  ["04", "Debt without dilution", "SBA, revenue-based financing, and lines of credit — without giving up equity."],
                  ["05", "Term sheet mastery", "Never lose your company in the fine print again."],
                  ["06", "The perpetual pipeline", "Build a lifetime rolodex of investors, funders & buyers."],
                ].map(([n, t, d]) => (
                  <div key={n} className="flex gap-4 border-t border-stone-100 pt-4 first:border-t-0 first:pt-0">
                    <div className="font-mono text-xs text-emerald-600">{n}</div>
                    <div>
                      <div className="text-sm font-medium text-slate-950">{t}</div>
                      <div className="mt-1 text-xs leading-relaxed text-stone-600">{d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="pointer-events-none absolute -bottom-4 -right-4 -z-10 h-full w-full rounded-lg border border-slate-950/10 bg-slate-950/5" />
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="border-b border-stone-200 bg-stone-50">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="label-mono mb-8">What Entrepreneurs Are Saying</div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { q: "I raised my first $500K in 90 days after applying the framework. Nothing I'd tried before came close.", a: "Founder, SaaS", loc: "Austin, TX" },
              { q: "I stopped chasing investors and started attracting them. This is the leverage move I wish I'd made 5 years ago.", a: "CEO, Consumer Brand", loc: "Chicago, IL" },
              { q: "The grants module alone paid for the entire program 20x over. Won 3 grants in one quarter.", a: "Founder, Impact Startup", loc: "Atlanta, GA" },
            ].map((t, i) => (
              <div key={i} className="card-flat">
                <Quote className="h-6 w-6 text-stone-300" />
                <p className="mt-4 text-sm leading-relaxed text-slate-950">"{t.q}"</p>
                <div className="mt-6 border-t border-stone-200 pt-4">
                  <div className="font-mono text-xs text-slate-950">{t.a}</div>
                  <div className="mt-0.5 font-mono text-[11px] text-stone-500">{t.loc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY NOW */}
      <section className="border-b border-stone-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 md:grid-cols-2 lg:px-8">
          <div>
            <div className="label-mono mb-4">Why Now</div>
            <h2 className="font-display text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">
              Capital is moving faster and more selectively than ever.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-stone-700">
              In 2026, the entrepreneurs who win are the ones who understand how money moves.
              Not the ones with the best idea. Not the ones who work the hardest.
              The ones who <span className="font-semibold text-slate-950">speak the language of capital fluently</span>.
            </p>
            <p className="mt-4 text-base leading-relaxed text-stone-700">
              This is the skill you learn once and use for the rest of your life. Every new venture.
              Every expansion. Every acquisition. Every legacy you leave.
            </p>
          </div>
          <div className="space-y-4">
            {[
              ["Compound Skill", "Learn once. Reuse across every business you'll ever build."],
              ["Direct ROI", "One raise typically returns 100x+ the cost of learning."],
              ["Investor Access", "Frameworks that get you in the room — not just on the list."],
              ["Grant Machine", "A repeatable system for winning non-dilutive capital."],
            ].map(([t, d]) => (
              <div key={t} className="rounded-md border border-stone-200 bg-stone-50 p-5">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                  <div className="font-display text-lg font-medium text-slate-950">{t}</div>
                </div>
                <p className="mt-2 pl-7 text-sm leading-relaxed text-stone-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHECKOUT CTA */}
      <section className="border-b border-stone-200 bg-slate-950 text-white">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center lg:px-8">
          <div className="label-mono !text-stone-500 mb-4">Enroll Today</div>
          <h2 className="font-display text-5xl font-bold tracking-tight sm:text-6xl">
            One skill.<br />
            <span className="text-emerald-400">A lifetime of capital.</span>
          </h2>
          <p className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-stone-300">
            Stop losing deals because you don't know the game. Get the playbook that has helped hundreds of entrepreneurs raise the capital they need to build the businesses they want.
          </p>
          <div className="mt-12 flex flex-col items-center gap-4">
            <a
              href={CHECKOUT_URL}
              target="_blank" rel="noreferrer"
              className="btn-primary !bg-emerald-500 !text-slate-950 hover:!bg-emerald-400 !px-10 !py-5 !text-base"
            >
              Get the Playbook Now <ArrowUpRight className="h-5 w-5" />
            </a>
            <div className="font-mono text-xs text-stone-500">Instant access • Lifetime updates</div>
          </div>
          <div className="mt-16 grid grid-cols-3 gap-8 border-t border-stone-800 pt-10 text-left">
            {[
              ["100+", "Deals closed by graduates"],
              ["$180M+", "Raised using the framework"],
              ["48hr", "Average time to first investor meeting"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-mono text-4xl font-medium text-white">{n}</div>
                <div className="label-mono !text-stone-500 mt-2">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTEGRATION WITH ATLAS */}
      <section className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <div className="label-mono mb-4">Part of the ATLAS™ Ecosystem</div>
            <h2 className="font-display text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">
              Built by Lanre Ajakaiye. Integrated into the AI Operating System.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-stone-600">
              Organizations running programs on ATLAS™ can bundle the AI Capital Raising System™ for their entire cohort.
              Every entrepreneur inside your program gets fluent in capital — automatically.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/request-demo?type=pricing" className="btn-secondary">Add to Your Program</Link>
              <Link to="/" className="btn-ghost">Explore ATLAS™</Link>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
